import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileStack } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";
import { FileTypeBadge, downloadForm } from "@/lib/forms";

export const RelatedForms = ({ kind, id }) => {
  const [forms, setForms] = useState([]);
  useEffect(() => { api.get(`/${kind}/${id}/forms`).then((r) => setForms(r.data)).catch(() => {}); }, [kind, id]);
  if (!forms.length) return null;
  return (
    <section className="mt-12" data-testid="related-forms">
      <h2 className="mb-4 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.14em]"><FileStack className="h-4 w-4" /> Related forms</h2>
      <div className="space-y-2">
        {forms.map((f) => (
          <div key={f.id} className="flex items-center gap-3 rounded-xl border border-lasso-line bg-white px-4 py-3 text-sm" data-testid={`related-form-${f.id}`}>
            <FileTypeBadge type={f.file_type} />
            <Link to={`/forms/${f.id}`} className="flex-1 font-medium hover:text-lasso-deep">{f.title} <span className="text-xs text-zinc-400">v{f.version}</span></Link>
            <button onClick={() => downloadForm(f).catch((e) => toast.error(formatApiError(e)))} className="rounded-full p-2 text-zinc-500 hover:bg-lasso-tint hover:text-lasso-ink" title="Download" data-testid={`related-form-download-${f.id}`}><Download className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </section>
  );
};

export const LinkedFormsField = ({ kind, id }) => {
  const [all, setAll] = useState([]);
  const [linked, setLinked] = useState([]);
  useEffect(() => {
    if (!id) return;
    api.get("/forms").then((r) => setAll(r.data));
    api.get(`/${kind}/${id}/forms`).then((r) => setLinked(r.data.map((f) => f.id)));
  }, [kind, id]);

  const toggle = async (fid, on) => {
    const next = on ? [...linked, fid] : linked.filter((x) => x !== fid);
    try {
      const { data } = await api.put(`/${kind}/${id}/forms`, { form_ids: next });
      setLinked(data.map((f) => f.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="rounded-2xl border border-lasso-line bg-white p-5" data-testid="linked-forms-field">
      <p className="mb-1 text-sm font-semibold">Related forms</p>
      {!id ? <p className="text-xs text-muted-foreground">Save first, then link forms.</p> : (
        <>
          <p className="mb-3 text-xs text-muted-foreground">Changes save instantly.</p>
          <div className="max-h-56 space-y-1.5 overflow-y-auto">
            {all.map((f) => (
              <label key={f.id} className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox checked={linked.includes(f.id)} onCheckedChange={(c) => toggle(f.id, !!c)} data-testid={`linked-form-${f.id}`} />
                <span className="flex-1 truncate">{f.title}</span><FileTypeBadge type={f.file_type} />
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
